/*
 * Simple chat interface for our agent.
 */
import { useEffect, useState } from 'react';

import { MemoryWidget } from '@/features/Minds/MemoryWidget';
import { OutputWidget } from '@/features/Minds/OutputWidget';
import { PlaceHolderQuestions } from '@/features/Minds/PlaceholderQuestions';
import { QuestionInput } from '@/features/Minds/QuestionInput';
import { UserQueryWidget } from '@/features/Minds/UserQueryWidget';
import { WorkflowWidget } from '@/features/Minds/WorkflowWidget';
import { createFileRoute } from '@tanstack/react-router';

import { useAutoScroll } from '@/hooks/useAutoScroll';
import {
    AgentOutput,
    UserQuery,
    WorkflowOutput,
    useMindsChat,
} from '@/hooks/useMindsChat';
import { useSettings } from '@/hooks/useSettings';

import { useLLMModels } from '@/lib/api';

import AiLoaderIcon from '@/components/ai-loader-icon';

export const Route = createFileRoute('/minds-agent')({
    component: MindsAgent,
});

function MindsAgent() {
    const { settings } = useSettings();
    const { data: llmModels } = useLLMModels();
    const {
        progress,
        meta,
        messages,
        sendMessage,
        updateAIModel,
        isRunning,
        latestMemory,
        cancel,
        reset,
        resetMemory,
    } = useMindsChat();
    const scrollRef = useAutoScroll(messages);

    const handleSend = (query: string) => {
        sendMessage({ query: query });
    };

    useEffect(() => {
        if (!llmModels || llmModels.length === 0) return;
        updateAIModel(llmModels[0]);
        reset();
    }, [llmModels]);

    const [inputValue, setInputValue] = useState<string>('');

    return (
        <div
            className="flex-1 overflow-hidden h-full relative lg:mx-0 mx-4 bg-white"
            data-testid="MindsAgent"
        >
            {/* Content */}
            <div
                ref={scrollRef}
                className="flex flex-col pt-8 items-center h-full overflow-auto sm:px-4 sm:pt-4 pb-[10em]"
            >
                <div className="max-w-[800px] flex flex-col gap-3 w-full">
                    {messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center min-h-full bg-background p-6">
                            <div className="w-full max-w-3xl space-y-8">
                                <header className="text-center space-y-2">
                                    <h1 className="text-3xl font-bold tracking-tight">
                                        What can I help you with today?
                                    </h1>
                                    <p className="text-paragraph text-gray-500">
                                        Find information from service bulletins
                                        including price, required tools,
                                        effectivity, and more
                                    </p>
                                </header>

                                <QuestionInput
                                    inputValue={inputValue}
                                    setInputValue={setInputValue}
                                    onSubmit={handleSend}
                                    popOver={
                                        settings.isDebug &&
                                        latestMemory && (
                                            <MemoryWidget
                                                memory={latestMemory}
                                            />
                                        )
                                    }
                                    isRunning={isRunning}
                                    onCancel={cancel}
                                    showNew={messages.length > 0}
                                    onNew={resetMemory}
                                />

                                <PlaceHolderQuestions
                                    onSuggestionClicked={handleSend}
                                />
                            </div>
                        </div>
                    ) : (
                        messages
                            .filter((m) => m.type !== 'WorkflowStart')
                            .filter((m) => m.type !== 'Memory')
                            .filter(
                                (m) =>
                                    m.type !== 'WorkflowOutput' ||
                                    settings.isDebug,
                            )
                            .map((msg, index) => (
                                <div key={index}>
                                    {msg.type === 'UserQuery' && (
                                        <UserQueryWidget
                                            userQuery={
                                                (msg as UserQuery).value.query
                                            }
                                        />
                                    )}
                                    {msg.type === 'WorkflowOutput' && (
                                        <WorkflowWidget
                                            flow={msg as WorkflowOutput}
                                        />
                                    )}
                                    {msg.type === 'AgentOutput' && (
                                        <OutputWidget
                                            output={msg as AgentOutput}
                                            conversationId={
                                                meta?.conversationId
                                            }
                                        />
                                    )}
                                </div>
                            ))
                    )}
                    {messages.length > 0 &&
                        messages.at(-1)?.type != 'WorkflowOutput' &&
                        isRunning && <AiLoaderIcon progress={progress} />}
                </div>
            </div>

            {messages.length > 0 && (
                <div className="absolute bottom-0 w-full flex justify-center pb-3 sm:p-4">
                    <div className="w-full max-w-3xl">
                        <QuestionInput
                            inputValue={inputValue}
                            setInputValue={setInputValue}
                            onSubmit={handleSend}
                            popOver={
                                settings.isDebug &&
                                latestMemory && (
                                    <MemoryWidget memory={latestMemory} />
                                )
                            }
                            isRunning={isRunning}
                            onCancel={cancel}
                            showNew={messages.length > 0}
                            onNew={resetMemory}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
